import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown, MessageBarActions, MessageBarBody, MessageBarTitle, Option } from '@fluentui/react-components';
import { GuideDrawer, GuideDrawerForm, GuideDrawerSection } from '../../../../theme/components/GuideDrawer';
import { GuideField } from '../../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';
import { GuideBannerAction } from '../../../../theme/components/GuideButtons';
import { GuideSkeleton } from '../../../../theme/components/GuideSkeleton';
import { InvoicePicker } from './PaymentInvoices.Picker';
import { UnavailableNotice, useInvoicesText } from './PaymentInvoices.Bits';
import {
    buildAllocationsPayload,
    invoiceLabel,
    invoiceOfAllocation,
    InvoiceSelection,
    isPendingInvoice,
    selectionFor,
    selectionsFromAllocations,
    suggestInvoicesForPayment,
    validateSelections
} from '../../../../functions/CreditorInvoices.Functions';
import { isNotFound } from '../../../../functions/PractitionerAccountStatement.Functions';
import {
    CreditorInvoice,
    CreditorInvoicesEntry,
    PaymentInvoiceAllocation,
    PaymentInvoiceAllocationInput,
    SetPaymentInvoicesResult
} from '../../../../models/Entities/CashFlow/CreditorInvoice/CreditorInvoice';
import { ErrorType } from '../../../../models/HttpError';
import './PaymentInvoices.Styles.css';

export interface PaymentOption {
    paymentId: number;
    label: string;
    /** Liquidaciones a las que se imputó el pago: sus facturas se sugieren primero. */
    settlementIds?: (number | null | undefined)[];
}

export interface PaymentInvoicesDrawerProps {
    open: boolean;
    onDismiss: () => void;
    /** Pago a editar. Sin pago, se elige de `paymentOptions` ("Asociar a pago" desde una factura). */
    paymentId?: number;
    paymentOptions?: PaymentOption[];
    /** Factura que se agrega al abrir (con lo pendiente precargado). */
    preselectInvoiceId?: number;
    /** Liquidaciones del pago (sugerencias) cuando se abre con `paymentId`. */
    suggestedSettlementIds?: (number | null | undefined)[];
    creditorId: string;
    creditorName: string;
    invoices: CreditorInvoicesEntry | undefined;
    settlementNameOf: (id: number) => string;
    LoadCreditorInvoices: (creditorId: string) => unknown;
    GetPaymentInvoices: (paymentId: number) => Promise<PaymentInvoiceAllocation[]>;
    SetPaymentInvoices: (paymentId: number, allocations: PaymentInvoiceAllocationInput[], creditorId?: string) => Promise<SetPaymentInvoicesResult>;
    onSaved?: (paymentId: number, count: number) => void;
}

type LoadState = 'idle' | 'loading' | 'loaded' | 'failed' | 'unavailable';

const errorText = (error: ErrorType | undefined, fallback: string) =>
    [error?.ErrorMessage, ...(error?.Errors ?? [])].filter(Boolean).filter((item, index, list) => list.indexOf(item) === index).join(' ') || fallback;

/**
 * "Facturas" de un pago: lista las que ya cubre, permite agregar o quitar y cambiar el importe
 * aplicado, y guarda con PUT payments/{id}/invoices (los avisos del servidor se muestran).
 */
export const PaymentInvoicesDrawer = (props: PaymentInvoicesDrawerProps) => {
    const tr = useInvoicesText();
    const [chosenPaymentId, setChosenPaymentId] = useState<number | undefined>(props.paymentId);
    const paymentId = props.paymentId ?? chosenPaymentId;
    const [loadState, setLoadState] = useState<LoadState>('idle');
    const [loadError, setLoadError] = useState<ErrorType | undefined>(undefined);
    const [allocations, setAllocations] = useState<PaymentInvoiceAllocation[]>([]);
    const [selections, setSelections] = useState<InvoiceSelection[]>([]);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<ErrorType | undefined>(undefined);
    const [warnings, setWarnings] = useState<string[] | undefined>(undefined);

    const invoices = useMemo(() => props.invoices?.data ?? [], [props.invoices?.data]);

    useEffect(() => {
        if (!props.open) return;
        setChosenPaymentId(props.paymentId);
        setSaveError(undefined);
        setWarnings(undefined);
        if (props.invoices?.status !== 'loading') props.LoadCreditorInvoices(props.creditorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open, props.paymentId, props.creditorId]);

    const loadAllocations = async (id: number) => {
        setLoadState('loading');
        setLoadError(undefined);
        try {
            const current = await props.GetPaymentInvoices(id);
            setAllocations(current);
            setSelections(selectionsFromAllocations(current));
            setLoadState('loaded');
        } catch (error: unknown) {
            setAllocations([]);
            setSelections([]);
            if (isNotFound(error)) setLoadState('unavailable');
            else {
                setLoadError(error as ErrorType);
                setLoadState('failed');
            }
        }
    };

    useEffect(() => {
        if (!props.open || !paymentId) {
            setLoadState('idle');
            return;
        }
        loadAllocations(paymentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open, paymentId]);

    // "Asociar a pago": la factura elegida entra con lo pendiente, sin pisar lo que el pago ya tenía.
    useEffect(() => {
        if (loadState !== 'loaded' || !props.preselectInvoiceId) return;
        const invoice = invoices.find((item) => item.id === props.preselectInvoiceId);
        if (!invoice) return;
        setSelections((current) => (current.some((item) => item.invoiceId === invoice.id) ? current : [...current, selectionFor(invoice)]));
    }, [loadState, props.preselectInvoiceId, invoices]);

    const settlementIds = props.paymentId
        ? props.suggestedSettlementIds ?? []
        : props.paymentOptions?.find((option) => option.paymentId === paymentId)?.settlementIds ?? [];

    // Las que ya cubre el pago (aunque estén pagadas) + las pendientes, sugeridas primero.
    const rows: CreditorInvoice[] = useMemo(() => {
        const allocated = allocations.map((item) => invoices.find((invoice) => invoice.id === item.creditorInvoiceId) ?? invoiceOfAllocation(item, props.creditorId));
        const pending = suggestInvoicesForPayment(invoices.filter(isPendingInvoice), settlementIds).filter((invoice) => !allocated.some((item) => item.id === invoice.id));
        return [...allocated, ...pending];
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allocations, invoices, settlementIds.join(',')]);

    const alreadyApplied = useMemo(
        () => Object.fromEntries(allocations.map((item) => [item.creditorInvoiceId, item.appliedAmount ?? 0])),
        [allocations]
    );

    const hasErrors = Object.keys(validateSelections(selections)).length > 0;
    const unchanged = JSON.stringify(buildAllocationsPayload(selections)) === JSON.stringify(buildAllocationsPayload(selectionsFromAllocations(allocations)));

    const save = async () => {
        if (!paymentId || hasErrors) return;
        setSaving(true);
        setSaveError(undefined);
        try {
            const result = await props.SetPaymentInvoices(paymentId, buildAllocationsPayload(selections), props.creditorId);
            props.onSaved?.(paymentId, selections.length);
            if (result.warnings.length > 0) {
                setWarnings(result.warnings);
                await loadAllocations(paymentId);
            } else {
                props.onDismiss();
            }
        } catch (error: unknown) {
            setSaveError(error as ErrorType);
        } finally {
            setSaving(false);
        }
    };

    const preselected = props.preselectInvoiceId ? invoices.find((item) => item.id === props.preselectInvoiceId) : undefined;
    const title = props.paymentId ? tr('paymentDrawer.title', { id: props.paymentId }) : tr('paymentDrawer.associateTitle');

    const renderBody = () => {
        if (!paymentId) return null;
        if (loadState === 'unavailable') return <UnavailableNotice what={tr('paymentDrawer.unavailableWhat')} testId="payment-invoices-unavailable" />;
        if (loadState === 'failed') {
            return (
                <GuideMessageBar intent="error" layout="multiline">
                    <MessageBarBody>
                        <MessageBarTitle>{tr('paymentDrawer.loadError')}</MessageBarTitle>
                        {errorText(loadError, '')}
                    </MessageBarBody>
                    <MessageBarActions>
                        <GuideBannerAction onClick={() => loadAllocations(paymentId)}>{tr('actions.retry')}</GuideBannerAction>
                    </MessageBarActions>
                </GuideMessageBar>
            );
        }
        if (props.invoices?.status === 'unavailable') return <UnavailableNotice what={tr('unavailableWhat')} testId="payment-invoices-unavailable" />;
        if (loadState !== 'loaded' || (props.invoices?.status === 'loading' && invoices.length === 0)) {
            return <GuideSkeleton rows={3} label={tr('picker.loading')} data-testid="payment-invoices-loading" />;
        }
        return (
            <InvoicePicker
                invoices={rows}
                value={selections}
                onChange={setSelections}
                suggestedSettlementIds={settlementIds}
                settlementNameOf={props.settlementNameOf}
                alreadyApplied={alreadyApplied}
                disabled={saving}
                testId="payment-invoices-picker"
            />
        );
    };

    return (
        <GuideDrawer
            open={props.open}
            size="large"
            title={title}
            eyebrow={tr('paymentDrawer.eyebrow', { name: props.creditorName })}
            onDismiss={props.onDismiss}
            preventDismiss={saving}
            closeLabel={tr('paymentDrawer.close')}
            testId="payment-invoices-drawer"
            secondaryAction={{ label: warnings ? tr('paymentDrawer.close') : tr('paymentDrawer.cancel'), disabled: saving }}
            primaryAction={{
                label: tr('paymentDrawer.save'),
                onClick: save,
                loading: saving,
                disabled: !paymentId || loadState !== 'loaded' || hasErrors || unchanged,
                testId: 'payment-invoices-save'
            }}
        >
            <GuideDrawerForm testId="payment-invoices-form">
                {saveError && (
                    <GuideMessageBar intent="error" layout="multiline" data-testid="payment-invoices-save-error">
                        <MessageBarBody>
                            <MessageBarTitle>{tr('paymentDrawer.saveError')}</MessageBarTitle>
                            {errorText(saveError, '')}
                        </MessageBarBody>
                    </GuideMessageBar>
                )}
                {warnings && (
                    <GuideMessageBar intent="warning" layout="multiline" data-testid="payment-invoices-warnings">
                        <MessageBarBody>
                            <MessageBarTitle>{tr('paymentDrawer.saved')} {tr('afterSave.warningsTitle')}</MessageBarTitle>
                            <ul className="pinv-results">{warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul>
                        </MessageBarBody>
                    </GuideMessageBar>
                )}
                {!props.paymentId && (
                    <GuideField
                        label={tr('paymentDrawer.choosePayment')}
                        required
                        hint={preselected ? tr('paymentDrawer.choosePaymentHint', { name: invoiceLabel(preselected) }) : undefined}
                    >
                        {(props.paymentOptions ?? []).length === 0 ? (
                            <span className="ichtys-secondary">{tr('paymentDrawer.noPayments')}</span>
                        ) : (
                            <Dropdown
                                aria-label={tr('paymentDrawer.choosePayment')}
                                placeholder={tr('paymentDrawer.choosePaymentPlaceholder')}
                                value={props.paymentOptions?.find((option) => option.paymentId === chosenPaymentId)?.label ?? ''}
                                selectedOptions={chosenPaymentId ? [String(chosenPaymentId)] : []}
                                onOptionSelect={(_event, data) => setChosenPaymentId(data.optionValue ? Number(data.optionValue) : undefined)}
                            >
                                {(props.paymentOptions ?? []).map((option) => (
                                    <Option key={option.paymentId} value={String(option.paymentId)} text={option.label}>{option.label}</Option>
                                ))}
                            </Dropdown>
                        )}
                    </GuideField>
                )}
                <GuideDrawerSection title={tr('picker.title')} description={tr('picker.description')}>
                    {renderBody()}
                </GuideDrawerSection>
            </GuideDrawerForm>
        </GuideDrawer>
    );
};

export default PaymentInvoicesDrawer;
